import {
  CalendarDefaultsProvider,
  type CalendarWeekStart,
  type HourCycle,
} from "@salestrak/ui";
import React from "react";
import { useUserSettings } from "#app/lib/hooks/use-user-settings";
import { setHourCycle } from "#app/lib/utils/format-date";
import { setWeekStartsOn } from "#app/lib/utils/date-presets";

/** Week start used when no preference is stored. */
export const DEFAULT_WEEK_START: CalendarWeekStart = 0; // Sunday

/**
 * The seven week starts with display labels — render these as the choices
 * anywhere the first day of the week is user-configurable (the preferences
 * screen's Select). Values are the stringified 0 (Sunday) – 6 (Saturday)
 * `Date#getDay` indices the preference stores as a number. This file is
 * their canonical home: it owns the provider that honors them.
 */
export const WEEK_START_OPTIONS: ReadonlyArray<{
  label: string;
  value: string;
}> = [
  { label: "Sunday", value: "0" },
  { label: "Monday", value: "1" },
  { label: "Tuesday", value: "2" },
  { label: "Wednesday", value: "3" },
  { label: "Thursday", value: "4" },
  { label: "Friday", value: "5" },
  { label: "Saturday", value: "6" },
];

/**
 * Resolves the stored preference to a usable week start. Settings rows are
 * JSON-valued (null while unloaded, arbitrary if a row was ever hand-edited),
 * so anything outside 0–6 falls back to the default.
 */
export function resolveWeekStart(
  pref: number | null | undefined,
): CalendarWeekStart {
  return pref != null && Number.isInteger(pref) && pref >= 0 && pref <= 6
    ? (pref as CalendarWeekStart)
    : DEFAULT_WEEK_START;
}

/** The stored `timeFormat` values with display labels — render these as
 *  the choices anywhere the clock is user-configurable (the preferences
 *  screen's Select). This file is their canonical home: it owns the
 *  provider that honors them. */
export const TIME_FORMAT_OPTIONS: ReadonlyArray<{
  label: string;
  value: string;
}> = [
  { label: "12-hour", value: "12h" },
  { label: "24-hour", value: "24h" },
];

export const DEFAULT_TIME_FORMAT = "12h";

/** Resolves the stored preference to a clock. Anything but "24h" — null
 *  while unloaded, never set, a hand-edited row — is the 12-hour default. */
export function resolveHourCycle(pref: string | null | undefined): HourCycle {
  return pref === "24h" ? 24 : 12;
}

/**
 * App-level calendar defaults: drives the library `CalendarDefaultsProvider`
 * from the user's firstDayOfWeek and timeFormat preferences, so every
 * Calendar, TimeField and DatePicker label — including the ones inside
 * popups — follows the viewer's week start and clock. The same clock is
 * published to the app's plain time formatters (`formatTime` and friends),
 * which run outside React and cannot read the context. Must render inside
 * AppProviders (needs Apollo).
 */
export function AppCalendarProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { preferences } = useUserSettings();
  const hourCycle = resolveHourCycle(preferences?.timeFormat);
  // Set during render, before any child formats a time: the formatters are
  // synchronous and the children below render after this line.
  setHourCycle(hourCycle);
  const weekStartsOn = resolveWeekStart(preferences?.firstDayOfWeek);
  // The same week start drives the "This Week" / "Last Week" date presets.
  setWeekStartsOn(weekStartsOn);
  return (
    <CalendarDefaultsProvider hourCycle={hourCycle} weekStartsOn={weekStartsOn}>
      {children}
    </CalendarDefaultsProvider>
  );
}
